import { Link } from 'react-router-dom';

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  categoryName: string;
  categorySlug: string;
  imageUrl: string;
  publishDate: string;
  readCount: number;
  isHot?: boolean;
  isNew?: boolean;
  variant?: 'default' | 'horizontal';
}

export default function ArticleCard({
  slug,
  title,
  excerpt,
  categoryName,
  categorySlug,
  imageUrl,
  publishDate,
  readCount,
  isHot,
  isNew,
  variant = 'default',
}: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <article className="flex flex-col sm:flex-row gap-4 p-4 bg-background-50 rounded-lg hover:bg-background-100 transition-colors group cursor-pointer">
        <Link to={`/article/${slug}`} className="shrink-0 w-full sm:w-[200px] h-[140px] rounded-lg overflow-hidden block cursor-pointer">
          <img
            src={imageUrl}
            alt={title}
            title={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </Link>
        <div className="flex flex-col justify-between flex-1 min-w-0">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Link
                to={`/category/${categorySlug}`}
                className="text-xs text-primary-500 bg-primary-100/70 px-2 py-0.5 rounded-full font-label cursor-pointer hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {categoryName}
              </Link>
              {isHot && (
                <span className="text-xs text-accent-600 bg-accent-100/80 px-2 py-0.5 rounded-full font-label whitespace-nowrap">
                  热门
                </span>
              )}
              {isNew && (
                <span className="text-xs text-secondary-600 bg-secondary-100/80 px-2 py-0.5 rounded-full font-label whitespace-nowrap">
                  最新
                </span>
              )}
            </div>
            <Link to={`/article/${slug}`} className="block cursor-pointer">
              <h3 className="text-base font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                {title}
              </h3>
            </Link>
            <p className="text-sm text-foreground-600 mt-1.5 line-clamp-2 leading-relaxed">
              {excerpt}
            </p>
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-foreground-500 font-label">
            <span>{publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {readCount >= 10000 ? `${(readCount / 10000).toFixed(1)}万` : readCount}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="bg-background-50 rounded-lg overflow-hidden hover:bg-background-100 transition-colors group cursor-pointer">
      <Link to={`/article/${slug}`} className="block cursor-pointer">
        <div className="relative w-full h-[200px] overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            title={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          {(isHot || isNew) && (
            <div className="absolute top-3 left-3 flex gap-1.5">
              {isHot && (
                <span className="text-xs bg-primary-500 text-background-50 px-2.5 py-0.5 rounded-full font-label">
                  热门
                </span>
              )}
              {isNew && (
                <span className="text-xs bg-accent-500 text-background-50 px-2.5 py-0.5 rounded-full font-label">
                  最新
                </span>
              )}
            </div>
          )}
        </div>
      </Link>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Link
            to={`/category/${categorySlug}`}
            className="text-xs text-primary-500 bg-primary-100/70 px-2 py-0.5 rounded-full font-label cursor-pointer hover:bg-primary-200 transition-colors whitespace-nowrap"
          >
            {categoryName}
          </Link>
        </div>
        <Link to={`/article/${slug}`} className="block cursor-pointer">
          <h3 className="text-base font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
            {title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
          {excerpt}
        </p>
        <div className="flex items-center justify-between mt-3 text-xs text-foreground-500 font-label">
          <span>{publishDate}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {readCount >= 10000 ? `${(readCount / 10000).toFixed(1)}万` : readCount}
          </span>
        </div>
      </div>
    </article>
  );
}